import { useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import {
  ApprovalTableCellInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import StoreOpenTables, {
  StoreOpenInteriorDetailDialog,
  type StoreOpenCellKey,
} from '@/components/Approval/@components/StoreOpen';
import type { ReplaceContentApi } from '@/components/LexicalEditor/plugins/ReplaceContentPlugin';
import { getStoreOpenDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import useStoreOpenInteriorDetail from '@/hooks/approval/useStoreOpenInteriorDetail';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  countStoreOpenBytes,
  createStoreOpenInputs,
  formatStoreOpenNumber,
  formatStoreOpenNumberInput,
  isStoreOpenDecimalKey,
  isStoreOpenDirectShop,
  STORE_OPEN_BYTE_LIMIT,
  STORE_OPEN_BYTE_LIMIT_MESSAGE,
  STORE_OPEN_BYTE_LIMITED_KEYS,
  STORE_OPEN_NUMBER_KEYS,
  storeOpenDetailTotal,
  storeOpenInputsFromDraftSource,
  storeOpenStoreFromDraftSource,
  trimStoreOpenBytes,
  type StoreOpenCandidate,
  type StoreOpenDraftSource,
  type StoreOpenFormState,
  type StoreOpenInputKey,
  type StoreOpenInteriorDetail,
  type StoreOpenKind,
} from '@/utils/approval/storeOpen';
import { buildStoreOpenAgencyContent } from './storeOpenAgencyContent';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const candidateKey = (
  c: Pick<
    StoreOpenCandidate,
    'brandCode' | 'season' | 'departmentDocId' | 'approvalDate'
  >,
) => `${c.brandCode}|${c.season}|${c.departmentDocId}|${c.approvalDate}`;

/** 레거시 콤보 문구 — (브랜드)매장명, 공사구분 : …, 시공완료일자 : … */
const candidateLabel = (c: StoreOpenCandidate, t: TFunction) =>
  `(${c.brandCode})${c.shopName ?? ''}, ${t('공사구분')} : ${
    c.constructionType ?? ''
  }, ${t('시공완료일자')} : ${c.constructionCompletedAt ?? ''}`;

const MAX_LENGTH: Partial<Record<StoreOpenInputKey, number>> = {
  interiorNote: 250,
  headquartersSupport: 250,
};

type Props = {
  /** 본문 에디터 mount 전에는 null — 양식 버튼이 본문을 바꾸므로 그때까지 버튼을 막음 */
  editorApi: ReplaceContentApi | null;
  /** 편집 — 저장 문서의 인테리어 상세 조회용 */
  documentId?: string;
  /** 어드민 「템플릿 저장」 본문 — 대리점 양식에만 씀. 비어 있으면 레거시 빈 표 */
  agencyTemplate?: string | null;
};

const hasTemplateContent = (html: string | null | undefined): html is string =>
  !!html &&
  (/<(table|img)\b/i.test(html) ||
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '') !== '');

/**
 * (구)매장 OPEN 품의(8) — 대리점은 빈 표를 본문에 넣고 직접 입력, 백화점·직영점은 매장 선택 후 「등록」으로 입력표.
 * 편집은 레거시처럼 양식·매장을 바꿀 수 없음
 */
const StoreOpenField = ({ editorApi, documentId, agencyTemplate }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'storeOpen' });
  const { options: brandOptions } = useMyBrandOptions();

  const [candidates, setCandidates] = useState<StoreOpenCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingStore, setIsLoadingStore] = useState(false);
  const [draftDetail, setDraftDetail] = useState<{
    detail: StoreOpenInteriorDetail | null;
    total: string;
  } | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const requestIdRef = useRef(0);

  const savedDetail = useStoreOpenInteriorDetail(
    documentId,
    isDetailOpen && !draftDetail,
  );

  if (!state) return null;
  if (state.locked && !state.store) return null;

  const update = (next: Partial<StoreOpenFormState>) => {
    const current = getValues('storeOpen');
    if (!current) return;
    setValue('storeOpen', { ...current, ...next }, { shouldDirty: true });
  };

  const updateInput = (key: StoreOpenInputKey, value: string) => {
    const current = getValues('storeOpen');
    if (!current) return;
    update({ inputs: { ...current.inputs, [key]: value } });
  };

  const resetSelection = () => {
    requestIdRef.current += 1;
    setCandidates([]);
    setSelectedKey('');
    setIsSearching(false);
    setIsLoadingStore(false);
    setDraftDetail(null);
  };

  const selectAgency = () => {
    resetSelection();
    update({
      kind: 'agency',
      store: null,
      inputs: createStoreOpenInputs(),
      loading: false,
    });
    editorApi?.replace(
      hasTemplateContent(agencyTemplate)
        ? agencyTemplate
        : buildStoreOpenAgencyContent(t),
    );
  };

  const selectStoreKind = async (kind: Exclude<StoreOpenKind, 'agency'>) => {
    resetSelection();
    update({
      kind,
      store: null,
      inputs: createStoreOpenInputs(),
      loading: false,
    });
    editorApi?.replace('');
    const requestId = requestIdRef.current;
    setIsSearching(true);
    try {
      const data = await getNestedDataFetcher<StoreOpenDraftSource>(
        getStoreOpenDraftSourceUrl(kind),
      );
      if (requestId !== requestIdRef.current) return;
      const list = data.display.candidates ?? [];
      if (list.length === 0) {
        update({ kind: null });
        snackbar.showError({ message: t('등록된 매장이 없습니다.') });
        return;
      }
      setCandidates(list);
      setSelectedKey(candidateKey(list[0]));
    } catch {
      if (requestId !== requestIdRef.current) return;
      update({ kind: null });
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsSearching(false);
    }
  };

  const loadStore = async () => {
    const candidate = candidates.find((c) => candidateKey(c) === selectedKey);
    const kind = getValues('storeOpen')?.kind;
    if (!candidate || (kind !== 'department' && kind !== 'direct')) return;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoadingStore(true);
    update({ loading: true });
    try {
      const data = await getNestedDataFetcher<StoreOpenDraftSource>(
        getStoreOpenDraftSourceUrl(kind, {
          brandCode: candidate.brandCode,
          season: candidate.season,
          departmentDocId: candidate.departmentDocId,
        }),
      );
      if (requestId !== requestIdRef.current) return;
      const store = storeOpenStoreFromDraftSource(data, candidate);
      update({
        store,
        inputs: storeOpenInputsFromDraftSource(data),
        loading: false,
      });
      setDraftDetail({
        detail: data.display.interiorDetail,
        total: storeOpenDetailTotal(
          store,
          data.display.interiorEstimate?.totalThousandWon ?? store.interiorCost,
        ),
      });
    } catch {
      if (requestId !== requestIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
      // 실패하면 표·저장 대상은 이전 매장 그대로 — 드롭다운도 그 매장으로 되돌려 화면과 맞춤
      const loaded = getValues('storeOpen')?.store;
      if (loaded) {
        setSelectedKey(
          candidateKey({
            brandCode: loaded.brandCode,
            season: loaded.season,
            departmentDocId: loaded.departmentDocId,
            approvalDate: loaded.approvalDate,
          }),
        );
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingStore(false);
        update({ loading: false });
      }
    }
  };

  const changeInput = (key: StoreOpenInputKey, raw: string) => {
    if (STORE_OPEN_NUMBER_KEYS.includes(key)) {
      updateInput(
        key,
        formatStoreOpenNumberInput(raw, isStoreOpenDecimalKey(key)),
      );
      return;
    }
    if (
      STORE_OPEN_BYTE_LIMITED_KEYS.includes(key) &&
      countStoreOpenBytes(raw) > STORE_OPEN_BYTE_LIMIT
    ) {
      snackbar.showError({ message: t(STORE_OPEN_BYTE_LIMIT_MESSAGE) });
      updateInput(key, trimStoreOpenBytes(raw));
      return;
    }
    updateInput(key, raw);
  };

  const { store } = state;

  const renderCell = (key: StoreOpenCellKey) => {
    if (!store) return null;
    switch (key) {
      case 'brand':
        return (
          store.brandName ??
          brandOptions.find((o) => o.value === store.brandCode)?.label ??
          store.brandCode
        );
      case 'shopName':
      case 'openDate':
      case 'shopArea':
      case 'shopForm':
      case 'constructionType':
      case 'interiorCompany':
        return store[key];
      case 'interiorCost':
        return store.isNoInterior
          ? ''
          : formatStoreOpenNumber(store.interiorCost);
      default:
        break;
    }
    if (store.isNoInterior && key === 'shopEtcCost') return null;
    if (store.isNoInterior && key === 'interiorNote') {
      return state.inputs.interiorNote;
    }
    const isNumber = STORE_OPEN_NUMBER_KEYS.includes(key);
    return (
      <ApprovalTableCellInput
        name={key}
        className={isNumber ? 'align-right' : undefined}
        value={state.inputs[key]}
        inputProps={{
          maxLength: MAX_LENGTH[key] ?? 100,
          inputMode: isNumber
            ? isStoreOpenDecimalKey(key)
              ? 'decimal'
              : 'numeric'
            : undefined,
        }}
        onChange={(e) => changeInput(key, e.target.value)}
      />
    );
  };

  const detailDialog = store && !store.isNoInterior && (
    <StoreOpenInteriorDetailDialog
      open={isDetailOpen}
      onClose={() => setIsDetailOpen(false)}
      detail={draftDetail ? draftDetail.detail : savedDetail.detail}
      legacyHtml={draftDetail ? null : savedDetail.legacyHtml}
      total={
        draftDetail
          ? draftDetail.total
          : storeOpenDetailTotal(store, store.interiorCost)
      }
      isLoading={!draftDetail && savedDetail.isLoading}
      isError={!draftDetail && savedDetail.isError}
    />
  );

  const tables = store && (
    <StoreOpenTables
      editable
      isDirectShop={isStoreOpenDirectShop(store)}
      isNoInterior={store.isNoInterior}
      renderCell={renderCell}
      onShowDetail={() => setIsDetailOpen(true)}
    />
  );

  if (state.locked) {
    return (
      <FieldBlock>
        {tables}
        {detailDialog}
      </FieldBlock>
    );
  }

  const isStoreKind = state.kind === 'department' || state.kind === 'direct';

  return (
    <FieldBlock>
      <ControlRow>
        <BoxButton
          type="button"
          variant={state.kind === 'agency' ? 'keyColor' : 'secondary'}
          size="medium"
          disabled={!editorApi}
          onClick={selectAgency}
        >
          {t('대리점 양식')}
        </BoxButton>
        <BoxButton
          type="button"
          variant={state.kind === 'department' ? 'keyColor' : 'secondary'}
          size="medium"
          disabled={!editorApi}
          onClick={() => selectStoreKind('department')}
        >
          {t('백화점 양식')}
        </BoxButton>
        <BoxButton
          type="button"
          variant={state.kind === 'direct' ? 'keyColor' : 'secondary'}
          size="medium"
          disabled={!editorApi}
          onClick={() => selectStoreKind('direct')}
        >
          {t('직영점 양식')}
        </BoxButton>
      </ControlRow>
      <NoteList>
        <li>
          {t('- 매장 구분에 맞는 양식을 선택하신 후 작성하시기 바랍니다.')}
        </li>
        <li className="is-warning">
          {t('- 필히 매장 레이아웃 파일을 첨부하십시오.')}
        </li>
      </NoteList>
      {isStoreKind && (isSearching || candidates.length > 0) && (
        <>
          <NoteList>
            <li>{t('- 매장을 선택하세요.')}</li>
          </NoteList>
          <ControlRow>
            <Select
              value={selectedKey}
              options={candidates.map((c) => ({
                value: candidateKey(c),
                label: candidateLabel(c, t),
              }))}
              placeholder={isSearching ? t('데이터를 불러오는 중입니다.') : ''}
              disabled={isSearching}
              onChange={(e) => setSelectedKey(String(e.target.value))}
              sx={{ ...approvalOutlineSelectSx, width: 500, maxWidth: '100%' }}
            />
            <BoxButton
              type="button"
              variant="secondary"
              size="medium"
              disabled={!selectedKey || isSearching || isLoadingStore}
              startIcon={
                isLoadingStore ? (
                  <Spinner theme="black" size="16px" />
                ) : undefined
              }
              onClick={loadStore}
            >
              {t('등록', { context: 'store' })}
            </BoxButton>
          </ControlRow>
        </>
      )}
      {isStoreKind && tables}
      {detailDialog}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const NoteList = styled('ul')({
  margin: '0 0 8px',
  padding: 0,
  listStyle: 'none',
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--dk-text, #1a1a1a)',
  '& .is-warning': { color: 'var(--dk-danger, #F55060)' },
});

export default StoreOpenField;
